import { Controller, type UseFormReturn } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { carePresentation } from './carePresentation';
import type { ProfileField } from './types';

export const ProfileCareFields = ({ form, fields, disabled }: {
  form: UseFormReturn<Record<string, string>>; fields: ProfileField[]; disabled: boolean;
}) => {
  const service = form.watch('service_type');
  const conference = form.watch('__careConference') === '1';
  const firstModifier = form.watch('billing_proceduremod1');
  const required = (name: string, value: string) => {
    if (['service_type', 'service_date', 'procedure_length', 'procedure_note'].includes(name)) return Boolean(value?.trim());
    if (form.getValues('service_type') !== 'ECM' || form.getValues('__careConference') === '1') return true;
    if (name === 'place_of_service' && form.getValues('billing_proceduremod1') === 'in_person') return true;
    return Boolean(value);
  };
  const select = (name: string) => {
    const metadata = fields.find(field => field.name === name && field.editable !== false);
    if (!metadata) return null;
    const id = `profile-coordinatedcare_notes-${name}-input`;
    const options = name === 'procedure_length' ? [...carePresentation.lengths] : [...metadata.options];
    const retained = form.getValues(name);
    if (retained && !options.includes(retained)) options.push(retained);
    return <div key={name}><Label htmlFor={id}>{carePresentation.labels[name]}</Label><Controller name={name} control={form.control}
      rules={{ validate: value => required(name, value) }} render={({ field }) => <Select value={field.value || '__empty'} disabled={disabled}
        onValueChange={value => { field.onChange(value === '__empty' ? '' : value); if (name === 'service_type' && value !== 'ECM') form.setValue('__careConference', '0', { shouldDirty: true }); }}>
        <SelectTrigger id={id} className="h-8"><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem id={`${id}-empty`} value="__empty">Select an option</SelectItem>{options.map(value => <SelectItem key={value} id={`${id}-${value.replace(/\W/g, '_')}`} value={value}>{name === 'procedure_length' ? `${value} minutes` : carePresentation.choices[value] ?? value}</SelectItem>)}
        </SelectContent>
      </Select>} /></div>;
  };
  const date = fields.find(field => field.name === 'service_date' && field.editable !== false);
  const note = fields.find(field => field.name === 'procedure_note' && field.editable !== false);
  return <div className="space-y-4">
    <div className="grid gap-4 md:grid-cols-2">{select('service_type')}{select('procedure_length')}
      {date && <div><Label htmlFor="profile-coordinatedcare_notes-service_date-input">Service date</Label><Input id="profile-coordinatedcare_notes-service_date-input" className="h-8" type="date" disabled={disabled} {...form.register('service_date', { required: true })} /></div>}
    </div>
    {service === 'ECM' && <>
      <div className="flex items-center gap-2"><Controller name="__careConference" control={form.control} render={({ field }) => <Checkbox
        id="profile-coordinatedcare_notes-conference" checked={field.value === '1'} disabled={disabled} onCheckedChange={checked => field.onChange(checked === true ? '1' : '0')} />} />
        <Label htmlFor="profile-coordinatedcare_notes-conference">Multidisciplinary team conference (G9007)</Label></div>
      {!conference && <div className="grid gap-4 md:grid-cols-3">{select('billing_proceduremod1')}{select('billing_proceduremod2')}{firstModifier !== 'in_person' && select('place_of_service')}</div>}
    </>}
    {note && <div><Label htmlFor="profile-coordinatedcare_notes-procedure_note-input">Procedure note</Label><Textarea id="profile-coordinatedcare_notes-procedure_note-input" rows={4} disabled={disabled} {...form.register('procedure_note', { validate: value => Boolean(value?.trim()) })} /></div>}
  </div>;
};
